import { useCallback } from 'react';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { useNotesAnimationContext } from 'modules/notes/providers';
import { useBulkUpdateTimeSignals } from '../../hooks/useBulkUpdateTimeSignals';
import { useBulkDeleteTimeSignals } from '../../hooks/useBulkDeleteTimeSignals';
import { useBulkClassificationUpdate } from '../../hooks/useBulkClassificationUpdate';
import { useFeatureFlag } from '../../../common/hooks';

export type NotesActions =
  | keyof typeof TimeSignalClassification
  | 'DeleteForever';

export type TimeSignalActionArgs = {
  notes: TimeSignal[];
  updates: {
    classificationTarget: NotesActions;
  };
};
export type HandleTimeSignalActionCallback = (
  args: TimeSignalActionArgs
) => Promise<void>;

type ReturnType = {
  handleTimeSignalAction: HandleTimeSignalActionCallback;
  isActionInProgress: boolean;
};

const mapNotesActionsToTimeSignalClassification = (action: NotesActions) => {
  if (action === 'Accepted') {
    return TimeSignalClassification.Accepted;
  }

  if (action === 'Private') {
    return TimeSignalClassification.Private;
  }

  if (action === 'Trash') {
    return TimeSignalClassification.Trash;
  }

  return TimeSignalClassification.Unclassified;
};

export const useTimeSignalActions = (
  clearSelectedNotes?: () => void
): ReturnType => {
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );

  const {
    onTimeSignalClassificationUpdate,
    updateInProgress
  } = useBulkUpdateTimeSignals();

  const {
    onTimeSignalsBulkDelete,
    deleteInProgress
  } = useBulkDeleteTimeSignals();
  const {
    onBulkClassificationUpdate,
    updateInProgress: bulkClassificationUpdateInProgress
  } = useBulkClassificationUpdate();

  const { setNotesToAnimate } = useNotesAnimationContext();

  const handleTimeSignalAction = useCallback(
    async ({ notes, updates }: TimeSignalActionArgs) => {
      setNotesToAnimate(
        notes.map(({ id }: TimeSignal) => ({
          id: id,
          action: 'Move',
          folder: updates.classificationTarget
        }))
      );
      if (updates.classificationTarget === 'DeleteForever') {
        await onTimeSignalsBulkDelete({
          selectedNoteIds: notes.map((note: TimeSignal) => note.id),
          unSelectedNoteIds: [],
          note: notes.length === 1 ? notes[0] : undefined
        });
      } else {
        if (isTimeSignalFederatedSuggestionsEnabled) {
          await onBulkClassificationUpdate({
            notes: notes,
            updates: {
              classificationTarget: mapNotesActionsToTimeSignalClassification(
                updates.classificationTarget
              )
            }
          });
        } else {
          await onTimeSignalClassificationUpdate({
            unSelectedNoteIds: [],
            selectedNoteIds: notes.map((note: TimeSignal) => note.id),
            classificationTarget: mapNotesActionsToTimeSignalClassification(
              updates.classificationTarget
            ),
            note: notes.length === 1 ? notes[0] : undefined
          });
        }
        if (clearSelectedNotes) clearSelectedNotes();
      }
      setNotesToAnimate([]);
    },
    [
      clearSelectedNotes,
      isTimeSignalFederatedSuggestionsEnabled,
      onBulkClassificationUpdate,
      onTimeSignalClassificationUpdate,
      onTimeSignalsBulkDelete,
      setNotesToAnimate
    ]
  );
  return {
    handleTimeSignalAction: handleTimeSignalAction,
    isActionInProgress:
      (isTimeSignalFederatedSuggestionsEnabled
        ? bulkClassificationUpdateInProgress
        : updateInProgress) || deleteInProgress
  };
};
